/**
 * Practice mode (USA-1544): the whole dashboard keeps working, but nothing is saved.
 *
 * While a practice session is open, every API call passes through here:
 * - Reads of a practice scenario's records are answered from its recording: real pages, real AI
 *   answers, recorded on the development database by `docs/guides/tools/record-practice.mjs`.
 * - Every other read goes to the real API, so lookups work on real data.
 * - Writes never reach the API. A click the scenario expects moves it to its next stage; an edit to
 *   a real record is kept in this browser tab only; anything else is refused with a plain message.
 *
 * The session lives in sessionStorage: it survives a reload and ends with the tab.
 */
import { API_BASE_URL } from '@/api/client'

export type PracticeTransition = {
  from: number
  to: number
  /** A button in the practice panel ("Supplier replies") moves the scenario on. */
  event?: string | null
  /** Or the trainee's own click on a page, matched by its request. */
  request?: { method: string; path: string } | null
  status?: number | null
  response?: unknown
}

export type PracticeFixture = {
  key: string
  recorded_at: string
  markers: string[]
  links: Record<string, string>
  states: { responses: Record<string, unknown> }[]
  transitions: PracticeTransition[]
}

export type PracticeSession = {
  lessonKey: string
  /** The recorded scenario, or null to practise on real records with nothing saved. */
  scenario: string | null
  stage: number
  step: number
  /** Edits made to real records during the session, by API path. */
  edits: Record<string, unknown>
  /** Writes the session has kept or refused, for steps that wait on one. */
  writes: number
  /** `writes` when the current step began. */
  stepWrites?: number
}

const SESSION_KEY = 'usars.practice-session'
/** The lesson key of practice mode opened from Learn without a lesson. */
export const FREE_PRACTICE = 'free'
export const PRACTICE_REFUSED =
  "You're in practice mode, so changes to real records aren't saved. End practice to make real changes."
export const PRACTICE_OFF_SCRIPT = 'This practice record only does what the practice steps ask. Try the next step.'

type Listener = (session: PracticeSession | null) => void
const listeners = new Set<Listener>()

export function readPracticeSession(): PracticeSession | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY)
    return raw ? (JSON.parse(raw) as PracticeSession) : null
  } catch {
    return null
  }
}

function writeSession(session: PracticeSession | null) {
  try {
    if (session) window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
    else window.sessionStorage.removeItem(SESSION_KEY)
  } catch {
    // Storage blocked: practice still runs for this page view.
  }
  for (const listener of listeners) listener(session)
}

let snapshotRaw: string | null = null
let snapshot: PracticeSession | null = null

/** The session as one stable object until it changes, for useSyncExternalStore. */
export function getPracticeSnapshot(): PracticeSession | null {
  let raw: string | null = null
  try {
    raw = window.sessionStorage.getItem(SESSION_KEY)
  } catch {
    raw = null
  }
  if (raw !== snapshotRaw) {
    snapshotRaw = raw
    snapshot = raw ? (JSON.parse(raw) as PracticeSession) : null
  }
  return snapshot
}

export function subscribePractice(listener: Listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function startPractice(lessonKey: string, scenario: string | null) {
  writeSession({ lessonKey, scenario, stage: 0, step: 0, edits: {}, writes: 0 })
}

export function endPractice() {
  writeSession(null)
}

export function updatePractice(change: Partial<PracticeSession>) {
  const session = readPracticeSession()
  if (session) writeSession({ ...session, ...change })
}

// ── Recorded scenarios ─────────────────────────────────────────────────────────────────────

const fixtures = new Map<string, Promise<PracticeFixture>>()
let realFetch: typeof fetch = (...args) => fetch(...args)

export function loadPracticeFixture(key: string, headers: HeadersInit): Promise<PracticeFixture> {
  let fixture = fixtures.get(key)
  if (!fixture) {
    fixture = realFetch(`${API_BASE_URL}/dashboard/learn/practice/${key}`, { headers }).then(async (response) => {
      if (!response.ok) throw new Error(`Practice scenario ${key} is unavailable (${response.status})`)
      return (await response.json()) as PracticeFixture
    })
    fixture.catch(() => fixtures.delete(key))
    fixtures.set(key, fixture)
  }
  return fixture
}

/** A practice event the panel can fire now ("Supplier replies"), if the scenario has one. */
export function nextEvent(fixture: PracticeFixture, stage: number) {
  return fixture.transitions.find((t) => t.from === stage && t.event)
}

export function fireEvent(fixture: PracticeFixture) {
  const session = readPracticeSession()
  if (!session) return
  const event = nextEvent(fixture, session.stage)
  if (event) writeSession({ ...session, stage: event.to })
}

const ISO_DATETIME = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2}(?:\.\d+)?)(Z|[+-]\d{2}:\d{2})?$/
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const DAY = 86_400_000

/** Move every date in a recording forward by whole days, so "2 days ago" stays 2 days ago. */
export function shiftDates<T>(value: T, days: number): T {
  if (!days) return value
  const shift = (v: unknown): unknown => {
    if (typeof v === 'string') {
      const datetime = ISO_DATETIME.exec(v)
      if (datetime) {
        const [, date, time, zone] = datetime
        const moved = new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10)
        return `${moved}T${time}${zone ?? ''}`
      }
      if (ISO_DATE.test(v)) return new Date(Date.parse(`${v}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10)
      return v
    }
    if (Array.isArray(v)) return v.map(shift)
    if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, shift(x)]))
    return v
  }
  return shift(value) as T
}

export function daysSinceRecording(fixture: PracticeFixture, now = Date.now()) {
  return Math.max(0, Math.floor((now - Date.parse(fixture.recorded_at)) / DAY))
}

/** The recorded answer to a read at this stage: this stage's, else the latest earlier one. */
export function recordedResponse(fixture: PracticeFixture, stage: number, relativeUrl: string) {
  const path = relativeUrl.split('?')[0] ?? relativeUrl
  for (let s = Math.min(stage, fixture.states.length - 1); s >= 0; s -= 1) {
    const responses = fixture.states[s]?.responses ?? {}
    if (`GET ${relativeUrl}` in responses) return { found: true, body: responses[`GET ${relativeUrl}`] }
  }
  // The same list with other filters or another page still shows the practice records.
  for (let s = Math.min(stage, fixture.states.length - 1); s >= 0; s -= 1) {
    const responses = fixture.states[s]?.responses ?? {}
    const key = Object.keys(responses).find((k) => k.slice(4).split('?')[0] === path)
    if (key) return { found: true, body: responses[key] }
  }
  return { found: false, body: undefined }
}

export function isPracticeRecord(fixture: PracticeFixture, relativeUrl: string) {
  return fixture.markers.some((marker) => relativeUrl.includes(marker))
}

// ── The fetch every request goes through ───────────────────────────────────────────────────

function json(body: unknown, status = 200) {
  return new Response(JSON.stringify(body ?? null), { status, headers: { 'Content-Type': 'application/json' } })
}

function requestParts(input: RequestInfo | URL, init?: RequestInit) {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
  return { url, method, headers }
}

async function readJsonBody(input: RequestInfo | URL, init?: RequestInit): Promise<unknown> {
  try {
    if (typeof init?.body === 'string') return JSON.parse(init.body)
    if (input instanceof Request) return await input.clone().json()
  } catch {
    // Not JSON (an upload): nothing to keep.
  }
  return undefined
}

export async function practiceFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const session = readPracticeSession()
  const { url, method, headers } = requestParts(input, init)
  if (!session || !url.startsWith(API_BASE_URL)) return realFetch(input, init)
  const relativeUrl = url.slice(API_BASE_URL.length)
  // Lesson progress and the recordings themselves are real.
  if (relativeUrl.startsWith('/dashboard/learn/')) return realFetch(input, init)

  const fixture = session.scenario ? await loadPracticeFixture(session.scenario, headers) : null
  const days = fixture ? daysSinceRecording(fixture) : 0

  if (method === 'GET' || method === 'HEAD') {
    if (relativeUrl in session.edits) return json(session.edits[relativeUrl])
    if (fixture) {
      const recorded = recordedResponse(fixture, session.stage, relativeUrl)
      if (recorded.found) return json(shiftDates(recorded.body, days))
    }
    return realFetch(input, init)
  }

  const path = relativeUrl.split('?')[0] ?? relativeUrl
  if (fixture) {
    const expected = fixture.transitions.find(
      (t) => t.from === session.stage && t.request?.method === method && t.request.path === path,
    )
    if (expected) {
      writeSession({ ...session, stage: expected.to, writes: session.writes + 1 })
      return json(shiftDates(expected.response, days), expected.status ?? 200)
    }
    if (isPracticeRecord(fixture, relativeUrl)) return json({ detail: PRACTICE_OFF_SCRIPT }, 409)
  }

  // An edit to a real record: keep it in this tab, shown wherever that record is read again.
  // `PATCH /manufacturers/x/address` edits fields of `/manufacturers/x`, so try the parent too.
  const body = await readJsonBody(input, init)
  if ((method === 'PATCH' || method === 'PUT') && body && typeof body === 'object' && !Array.isArray(body)) {
    for (const target of [path, path.replace(/\/[^/]+$/, '')]) {
      const current: unknown =
        session.edits[target] ??
        (await realFetch(`${API_BASE_URL}${target}`, { headers })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null))
      if (current && typeof current === 'object' && !Array.isArray(current)) {
        const edited = { ...current, ...body }
        writeSession({ ...session, edits: { ...session.edits, [target]: edited }, writes: session.writes + 1 })
        return json(edited)
      }
    }
  }
  writeSession({ ...session, writes: session.writes + 1 })
  return json({ detail: PRACTICE_REFUSED }, 409)
}

/** Route every request through practice mode. Called once, before the app renders. */
export function installPracticeFetch() {
  if (typeof window === 'undefined') return
  const original = window.fetch.bind(window)
  realFetch = original
  window.fetch = practiceFetch
}
